import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { Permission } from '@pizza-roma/shared';
import { useAuth } from './lib/auth';
import { LangToggle, useI18n } from './i18n';
import { PageLoading } from './components/ui';
import { PublicLayout } from './features/public/PublicLayout';
import { LandingPage } from './features/public/LandingPage';
import { MenuPage } from './features/public/MenuPage';
import { CartPage } from './features/public/CartPage';
import { CheckoutPage } from './features/public/CheckoutPage';
import { TrackingPage } from './features/public/TrackingPage';
import { AccountPage } from './features/public/AccountPage';
import { InstallPage } from './features/public/InstallPage';
import { LoginPage } from './features/auth/LoginPage';

const BackOffice = lazy(() => import('./features/backoffice/BackOffice'));
const DriverApp = lazy(() => import('./features/driver/DriverApp'));
const PlatformApp = lazy(() => import('./features/platform/PlatformApp'));

/** Client-side guard for navigation only; every API call is authorized again on the server. */
export function RequireAuth({ children, permission, platform }: { children: ReactNode; permission?: Permission; platform?: boolean }) {
  const { me, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <PageLoading />;
  if (!me?.user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  const ok = platform ? me.user.isPlatformAdmin : permission ? me.permissions?.includes(permission) : true;
  if (!ok) return <Forbidden />;
  return <>{children}</>;
}

function Forbidden() {
  const { logout } = useAuth();
  const { t } = useI18n();
  return (
    <div className="page-loading">
      <div className="card card-pad stack center" style={{ maxWidth: 420 }} data-testid="forbidden">
        <LangToggle className="center-self" />
        <h2>{t.app.accessDenied}</h2>
        <p className="muted">{t.app.noAccess}</p>
        <button className="btn" onClick={() => void logout().then(() => (window.location.href = '/login'))}>
          {t.app.switchAccount}
        </button>
      </div>
    </div>
  );
}

export function App() {
  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        <Route path="/" element={<Navigate to="/r/pizzeria-fornavia" replace />} />
        <Route path="/r/:slug" element={<PublicLayout />}>
          <Route index element={<LandingPage />} />
          <Route path="menu" element={<MenuPage />} />
          <Route path="product/:productId" element={<MenuPage />} />
          <Route path="cart" element={<CartPage />} />
          <Route path="checkout" element={<CheckoutPage />} />
          <Route path="order/:token" element={<TrackingPage />} />
          <Route path="account" element={<AccountPage />} />
          <Route path="install" element={<InstallPage />} />
        </Route>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/restaurant/*" element={<RequireAuth><BackOffice /></RequireAuth>} />
        <Route path="/driver/*" element={<RequireAuth permission="driver.deliver"><DriverApp /></RequireAuth>} />
        <Route path="/platform/*" element={<RequireAuth platform><PlatformApp /></RequireAuth>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
